import React from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import Client from "./Client";
import Config from "./ClientConfig";
import OptimizedConfigImage from "./OptimizedConfigImage";
import { getTextConfigValue } from "../i18n";

export function toConfigMediaUrl(path) {
  if (!path) return "";
  if (String(path).startsWith("http://") || String(path).startsWith("https://")) {
    return path;
  }

  return `${Config.BASE_URL}${String(path).startsWith("/") ? "" : "/"}${path}`;
}

export function getMobileImageUrl(data, key = "LOGO") {
  const list = Array.isArray(data) ? data : data?.MobileAppImageConfigs || [];
  if (!Array.isArray(list)) return "";

  const item = list.find((x) => x?.key === key) || null;
  return toConfigMediaUrl(item?.image || "");
}

export function getMobileTextValue(data, fallback = "", selectedLanguage, defaultLanguage) {
  return getTextConfigValue(data, fallback, selectedLanguage, defaultLanguage);
}

export function fetchMobileText(client, key, language) {
  const query = language ? { key, language } : { key };
  return client.getData(Config.API_URL_CONFIG_MOBILEAPP_TEXT, query);
}

export function fetchMobileImage(client, key = "LOGO") {
  return client.getData(Config.API_URL_CONFIG_MOBILEAPP_IMAGE, { key });
}

export function useMobileBrand(language, defaultLanguage) {
  const client = React.useMemo(() => new Client("none"), []);
  const [brand, setBrand] = React.useState({ logoUrl: "", brandName: "" });

  React.useEffect(() => {
    const logoData = fetchMobileImage(client, "LOGO");
    const brandNameData = fetchMobileText(client, "BRAND_NAME", language);

    setBrand({
      logoUrl: getMobileImageUrl(logoData, "LOGO"),
      brandName: getMobileTextValue(brandNameData, "", { iref: language }, defaultLanguage),
    });
  }, [client, language, defaultLanguage]);

  return brand;
}

export function MobileStandalonePage({
  children,
  logoUrl,
  brandName,
  title,
  subtitle,
  center = false,
  maxWidth = 440,
}) {
  const showHeader = Boolean(logoUrl || title || subtitle);

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        width: "100%",
        maxWidth: "100vw",
        boxSizing: "border-box",
        display: "flex",
        alignItems: center ? "center" : "flex-start",
        justifyContent: "center",
        px: { xs: 1.5, sm: 2 },
        pt: "max(18px, env(safe-area-inset-top))",
        pb: "max(18px, env(safe-area-inset-bottom))",
        overflowX: "hidden",
        overflowY: "auto",
        color: "#171717",
        background:
          "linear-gradient(180deg, #fffaf0 0%, #f6f8ee 52%, #edf7f4 100%)",
        fontFamily:
          'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial',
      }}
    >
      <Box sx={{ width: "100%", maxWidth, boxSizing: "border-box" }}>
        {showHeader ? (
          <Stack spacing={1.15} alignItems="center" sx={{ mb: 2 }}>
            {logoUrl ? (
              <Box
                sx={{
                  width: 68,
                  height: 68,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(255,255,255,0.86)",
                  border: "1px solid rgba(116,74,22,0.10)",
                  boxShadow: "0 18px 44px rgba(116,74,22,0.12)",
                  overflow: "hidden",
                }}
              >
                <OptimizedConfigImage
                  src={logoUrl}
                  alt={brandName ? `${brandName} logo` : "Restaurant logo"}
                  style={{ maxWidth: 52, maxHeight: 52, objectFit: "contain" }}
                />
              </Box>
            ) : null}

            {title ? (
              <Typography
                component="h1"
                sx={{
                  color: "#171717",
                  fontSize: { xs: 24, sm: 28 },
                  lineHeight: 1.12,
                  fontWeight: 900,
                  letterSpacing: 0,
                  textAlign: "center",
                }}
              >
                {title}
              </Typography>
            ) : null}

            {subtitle ? (
              <Typography
                sx={{
                  maxWidth: 340,
                  color: "rgba(23,23,23,0.68)",
                  fontSize: 14,
                  lineHeight: 1.5,
                  fontWeight: 600,
                  letterSpacing: 0,
                  textAlign: "center",
                }}
              >
                {subtitle}
              </Typography>
            ) : null}
          </Stack>
        ) : null}

        {children}
      </Box>
    </Box>
  );
}

export function MobileSurface({ children, sx }) {
  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        boxSizing: "border-box",
        p: { xs: 2, sm: 2.5 },
        borderRadius: "8px",
        bgcolor: "rgba(255,255,255,0.92)",
        border: "1px solid rgba(116,74,22,0.10)",
        boxShadow: "0 18px 46px rgba(116,74,22,0.10)",
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
}

export function MobileInfoRow({ label, children }) {
  return (
    <Box
      sx={{
        py: 1.25,
        borderTop: "1px solid rgba(116,74,22,0.10)",
        "&:first-of-type": { borderTop: 0, pt: 0 },
      }}
    >
      <Typography
        sx={{
          mb: 0.35,
          color: "rgba(23,23,23,0.56)",
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: 0,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: "#171717",
          fontSize: 15,
          lineHeight: 1.45,
          fontWeight: 750,
          overflowWrap: "anywhere",
          wordBreak: "break-word",
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

export function MobileHtmlContent({ html }) {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        color: "#1f1f1f",
        fontSize: 15,
        lineHeight: 1.72,

        "& *": {
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
          whiteSpace: "normal",
        },

        "& h1, & h2, & h3": {
          mt: 2.25,
          mb: 1,
          color: "#171717",
          fontWeight: 900,
          lineHeight: 1.18,
          letterSpacing: 0,
        },
        "& h1:first-of-type, & h2:first-of-type, & h3:first-of-type": {
          mt: 0,
        },
        "& h1": { fontSize: 24 },
        "& h2": { fontSize: 20 },
        "& h3": { fontSize: 17 },
        "& p": { m: "0 0 12px 0" },
        "& p > br:only-child": { display: "none" },
        "& ul, & ol": { pl: 2.5, my: 1.25 },
        "& li": { mb: 0.65 },
        "& a": {
          color: "#9a4f12",
          fontWeight: 800,
          textDecoration: "underline",
          textUnderlineOffset: "3px",
        },
        "& img": {
          display: "block",
          maxWidth: "100%",
          height: "auto",
          borderRadius: "8px",
        },
        "& pre, & code": {
          display: "block",
          maxWidth: "100%",
          overflowX: "auto",
          whiteSpace: "pre-wrap",
        },
        "& table": {
          display: "block",
          width: "100%",
          maxWidth: "100%",
          overflowX: "auto",
          borderCollapse: "collapse",
        },
        "& th, & td": {
          borderBottom: "1px solid rgba(23,23,23,0.10)",
          p: 1,
          textAlign: "left",
          verticalAlign: "top",
        },
      }}
      dangerouslySetInnerHTML={{ __html: html || "" }}
    />
  );
}
